import { type FC, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/ui/use-toast";

const deleteAccountSchema = z.object({
	password: z.string().min(1, { message: "パスワードを入力してください" }),
});

type DeleteAccountValues = z.infer<typeof deleteAccountSchema>;

export const DeleteAccountForm: FC = () => {
	const { toast } = useToast();

	const [isOpen, setIsOpen] = useState(false);

	const form = useForm<DeleteAccountValues>({
		resolver: zodResolver(deleteAccountSchema),
	});

	const onSubmit = async (data: DeleteAccountValues) => {
		try {
			// TODO: アカウント削除のAPI呼び出し
			toast({
				title: "アカウントを削除しました",
				description: "ご利用ありがとうございました",
			});
		} catch (error) {
			toast({
				title: "エラーが発生しました",
				description: "アカウントの削除に失敗しました",
				variant: "destructive",
			});
		}
	};

	return (
		<div className="space-y-6">
			<div className="text-sm text-muted-foreground">
				アカウントを削除すると、すべてのデータが完全に削除され、復元することはできません。
			</div>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel>現在のパスワード</FormLabel>
								<FormControl>
									<Input type="password" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<AlertDialog open={isOpen} onOpenChange={setIsOpen}>
						<AlertDialogTrigger asChild>
							<Button variant="destructive">アカウントを削除</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									本当にアカウントを削除しますか？
								</AlertDialogTitle>
								<AlertDialogDescription>
									この操作は取り消すことができません。すべてのデータが完全に削除されます。
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>キャンセル</AlertDialogCancel>
								<AlertDialogAction
									onClick={form.handleSubmit(onSubmit)}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									削除する
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</form>
			</Form>
		</div>
	);
};
